import { AlertCircle, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useReportQuery } from '@/hooks/useReportQuery';
import { CustomReportVisualization } from './CustomReportVisualization';
import type { ReportConfig } from '@/types/custom-reports';

interface ReportLivePreviewProps {
  config: ReportConfig;
  isVisible?: boolean;
  onToggle?: () => void;
}

/**
 * Live preview component for report data in the builder.
 * Shows real-time data based on current configuration without saving.
 */
export function ReportLivePreview({
  config,
  isVisible = true,
  onToggle,
}: ReportLivePreviewProps) {
  // Only fetch when preview is visible and config has data sources
  const shouldFetch = isVisible && config.dataSources && config.dataSources.length > 0;
  const {
    data: queryResult,
    isLoading,
    error,
    refetch,
  } = useReportQuery(shouldFetch ? config : null, {
    staleTime: 30000, // 30 seconds for preview (shorter cache)
  });

  if (!isVisible) {
    return (
      <Card className="border-dashed">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <EyeOff className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground text-center max-w-sm">
            Visualização de prévia oculta. Clique em "Mostrar Prévia" para ver os dados em tempo real.
          </p>
          {onToggle && (
            <Button variant="outline" onClick={onToggle} className="mt-4">
              <Eye className="h-4 w-4 mr-2" />
              Mostrar Prévia
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Prévia em Tempo Real
            </CardTitle>
            <CardDescription>
              Visualização dos dados com base na configuração atual (não salva)
            </CardDescription>
          </div>
          {onToggle && (
            <Button variant="ghost" size="sm" onClick={onToggle}>
              <EyeOff className="h-4 w-4 mr-2" />
              Ocultar
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Carregando prévia dos dados...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {!isLoading && error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Erro ao carregar prévia</AlertTitle>
            <AlertDescription className="flex items-center justify-between">
              <span>
                Não foi possível carregar os dados para prévia. Verifique se os filtros estão corretos.
              </span>
              <Button
                onClick={() => refetch()}
                variant="outline"
                size="sm"
                className="ml-4"
              >
                Tentar Novamente
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* No Data State */}
        {!isLoading && !error && queryResult && queryResult.data.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-muted-foreground max-w-sm">
              Nenhum dado encontrado para os filtros selecionados. Tente ajustar o período ou os filtros.
            </p>
          </div>
        )}

        {/* Preview Content */}
        {!isLoading && !error && queryResult && queryResult.data.length > 0 && (
          <div className="border-t pt-6">
            <div className="mb-4 text-sm text-muted-foreground">
              Mostrando {queryResult.data.length} {queryResult.data.length === 1 ? 'registro' : 'registros'}
            </div>
            <CustomReportVisualization
              data={queryResult.data}
              config={config}
              comparisonData={queryResult.comparison?.previous?.data}
            />
          </div>
        )}

        {/* No config/data sources */}
        {!isLoading && !shouldFetch && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-muted-foreground max-w-sm">
              Selecione pelo menos uma fonte de dados para ver a prévia.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
